import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../apiClient';
import BandPciHistogram from './graphs/BandPciHistogram';
import SignalTimeSeries, { SeriesPoint } from './graphs/SignalTimeSeries';
import SignalCdf from './graphs/SignalCdf';

interface GraphOverlayProps {
  selectedRows: string[];
  onClose: () => void;
}

interface GraphData {
  carriers: string[];
  lte: {
    band_counts: Record<string, Record<string, number>>;
    pci_counts: Record<string, Record<string, number>>;
    rsrp_series: SeriesPoint[];
    rsrq_series: SeriesPoint[];
  };
  nr: {
    band_counts: Record<string, Record<string, number>>;
    pci_counts: Record<string, Record<string, number>>;
    rsrp_series: SeriesPoint[];
    rsrq_series: SeriesPoint[];
  };
}

export default function GraphOverlay({ selectedRows, onClose }: GraphOverlayProps) {
  const [data, setData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [xAxisMode, setXAxisMode] = useState<'sample' | 'time'>('sample');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiClient.post('/sigcap/api/graph-data/', {
          collection_ids: selectedRows,
        });
        setData(res.data);
      } catch {
        setError('Failed to load graph data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedRows]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 1300,
        overflowY: 'auto',
        p: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Cellular Graphs
        </Typography>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
          {error}
        </Typography>
      )}

      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <BandPciHistogram
            carriers={data.carriers}
            lteBandCounts={data.lte.band_counts}
            ltePciCounts={data.lte.pci_counts}
            nrBandCounts={data.nr.band_counts}
            nrPciCounts={data.nr.pci_counts}
          />
          <Divider />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Primary RSRP / RSRQ Graphs</Typography>
              <ToggleButtonGroup
                value={xAxisMode}
                exclusive
                onChange={(_, val) => { if (val) setXAxisMode(val); }}
                size="small"
              >
                <ToggleButton value="sample">Sample #</ToggleButton>
                <ToggleButton value="time">Time</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <SignalTimeSeries
              title="Primary RSRP"
              lteSeries={data.lte.rsrp_series}
              nrSeries={data.nr.rsrp_series}
              carriers={data.carriers}
              yLabel="dBm"
              yDomain={[-140, -44]}
              timeAxis={xAxisMode === 'time'}
            />
            <Divider sx={{ my: 3 }} />
            <SignalTimeSeries
              title="Primary RSRQ"
              lteSeries={data.lte.rsrq_series}
              nrSeries={data.nr.rsrq_series}
              carriers={data.carriers}
              yLabel="dB"
              yDomain={[-20, -3]}
              timeAxis={xAxisMode === 'time'}
            />
          </Box>
          <Divider />
          <SignalCdf
            title="Primary RSRP CDF"
            lteSeries={data.lte.rsrp_series}
            nrSeries={data.nr.rsrp_series}
            carriers={data.carriers}
            xLabel="dBm"
            xDomain={[-140, -44]}
          />
          <Divider />
          <SignalCdf
            title="Primary RSRQ CDF"
            lteSeries={data.lte.rsrq_series}
            nrSeries={data.nr.rsrq_series}
            carriers={data.carriers}
            xLabel="dB"
            xDomain={[-20, -3]}
          />
        </Box>
      )}
    </Box>
  );
}
